<template>
  <div class="confirm-form">
    <section class="confirm-section">
      <h2 class="section-title">{{ t("contestEntryForm.step3.applicantSection") }}</h2>
      <dl class="confirm-fields">
        <div v-for="field in applicantFields" :key="field.key" class="confirm-field">
          <dt class="confirm-label">{{ field.label }}</dt>
          <dd class="confirm-value" :class="{ 'confirm-value-multiline': field.multiline }">{{ field.value }}</dd>
        </div>
      </dl>
    </section>

    <section class="confirm-section">
      <h2 class="section-title">{{ t("contestEntryForm.step3.workSection") }}</h2>
      <dl class="confirm-fields">
        <div v-if="categoryLabel" class="confirm-field">
          <dt class="confirm-label">{{ t("contestEntryDataForm.category") }}</dt>
          <dd class="confirm-value">{{ categoryLabel }}</dd>
        </div>
        <div class="confirm-field">
          <dt class="confirm-label">{{ t("contestEntryForm.step3.selectedWork") }}</dt>
          <dd class="confirm-value">
            <div class="selected-work-card">
              <ImageView class="selected-work-cover" :src="workForm.novelImage" />
              <p class="selected-work-title">{{ workForm.workTitle }}</p>
            </div>
          </dd>
        </div>
        <div class="confirm-field">
          <dt class="confirm-label">{{ t("contestEntryForm.step2.synopsis.label") }}</dt>
          <dd class="confirm-value confirm-value-multiline">{{ workForm.synopsis }}</dd>
        </div>
        <div v-for="field in extraFieldItems" :key="field.key" class="confirm-field">
          <dt class="confirm-label">{{ field.title }}</dt>
          <dd class="confirm-value confirm-value-multiline">{{ workForm.extraAnswers?.[field.key] ?? "" }}</dd>
        </div>
      </dl>
    </section>

    <p v-if="submitError" class="submit-error" role="alert">{{ submitError }}</p>

    <div class="form-footer">
      <button type="button" class="back-button" @click="emit('back')">
        {{ t("contestEntryForm.step3.back") }}
      </button>
      <button type="button" class="submit-button" :disabled="submitting" @click="emit('submit')">
        {{ submitting ? t("contestEntryForm.step3.submitting") : t("contestEntryForm.step3.submit") }}
      </button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, defineEmits, defineProps } from "vue";
import { useI18n } from "vue-i18n-bridge";
import { Gender } from "@/lib/models/nolaContest";
import type { NolaPfContestExtraFieldItem } from "@/lib/models/nolaPfContest";
import ImageView from "@/components/atoms/ImageView.vue";
import type { ContestEntryWorkFormValue } from "@/components/organisms/event/contestEntryWorkFormTypes";
import type { ContestEntryApplicantFormValue } from "./contestEntryApplicantInfoFormEventHandlers";

const props = defineProps<{
  applicantForm: ContestEntryApplicantFormValue;
  workForm: ContestEntryWorkFormValue;
  categoryLabel: string;
  extraFieldItems: NolaPfContestExtraFieldItem[];
  submitting: boolean;
  submitError: string;
}>();

const emit = defineEmits<{
  (e: "back"): void;
  (e: "submit"): void;
}>();

const { t } = useI18n();

function formatBirthDateDisplay(birthDate: string): string {
  const matched = birthDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!matched) return birthDate;
  return t("contestEntryForm.step3.birthDateFormat", {
    year: matched[1],
    month: Number(matched[2]),
    day: Number(matched[3]),
  });
}

function formatGenderLabel(gender: number): string {
  if (gender === Gender.male) return t("common.gender.male");
  if (gender === Gender.female) return t("common.gender.female");
  if (gender === Gender.other) return t("common.gender.other");
  return t("common.unselected");
}

const applicantFields = computed(() => {
  const fields: { key: string; label: string; value: string; multiline?: boolean }[] = [
    { key: "name", label: t("contestEntryForm.step1.name.label"), value: props.applicantForm.name },
    { key: "nameKana", label: t("contestEntryForm.step1.nameKana.label"), value: props.applicantForm.nameKana },
    { key: "penname", label: t("contestEntryForm.step1.penname.label"), value: props.applicantForm.penname },
    { key: "email", label: t("contestEntryForm.step1.email.label"), value: props.applicantForm.email },
    { key: "phone", label: t("contestEntryForm.step1.phone.label"), value: props.applicantForm.phone },
    {
      key: "gender",
      label: t("contestEntryForm.step1.gender.label"),
      value: formatGenderLabel(props.applicantForm.gender),
    },
    {
      key: "birthDate",
      label: t("contestEntryForm.step1.birthDate.label"),
      value: formatBirthDateDisplay(props.applicantForm.birthDate),
    },
    {
      key: "historyOfAwards",
      label: t("contestEntryForm.step1.historyOfAwards.label"),
      value: props.applicantForm.historyOfAwards,
      multiline: true,
    },
  ];
  return fields;
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.confirm-form {
  display: flex;
  flex-direction: column;
}

.confirm-section + .confirm-section {
  margin-top: 32px;
}

.section-title {
  margin: 0 0 16px;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-default);
}

.confirm-fields {
  margin: 0;
}

.confirm-field {
  margin: 0 0 24px;

  &:last-child {
    margin-bottom: 0;
  }
}

.confirm-label {
  margin: 0 0 4px;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-default);
  color: var(--color-text-normal);
}

.confirm-value {
  margin: 0;
  font-size: var(--font-size-md);
  line-height: var(--line-height-default);
  color: var(--color-text-normal);
}

.confirm-value-multiline {
  white-space: pre-wrap;
  word-break: break-word;
}

.selected-work-card {
  display: flex;
  align-items: center;
  gap: 16px;
  min-height: 91px;
  padding: 8px 16px;
  border-radius: var(--radius-sm);
  background-color: var(--color-surface-layer);
}

.selected-work-cover {
  flex-shrink: 0;
  width: 40px;
  height: 59px;

  :deep(.image) {
    width: 40px;
    height: 59px;
    object-fit: cover;
  }
}

.selected-work-title {
  flex: 1;
  min-width: 0;
  margin: 0;
  font-size: var(--font-size-sm);
  line-height: var(--line-height-default);
  word-break: break-word;
}

.form-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 40px;
}

.back-button {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 0 24px;
  border: none;
  border-radius: var(--radius-rounded);
  background-color: var(--color-surface-base);
  color: var(--color-text-normal);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  cursor: pointer;

  @include hover-overlay;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-focus-ring);
  }
}

.submit-button {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 0 24px;
  border: none;
  border-radius: var(--radius-rounded);
  background-color: var(--color-primary);
  color: var(--color-text-on-fixed-bg);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  cursor: pointer;

  @include hover-overlay;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-focus-ring);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.44;
  }
}

.submit-error {
  margin: 24px 0 0;
  font-size: var(--font-size-sm);
  line-height: var(--line-height-default);
  color: var(--color-warning);
}

@include mq("sp") {
  .form-footer {
    margin-top: 24px;
  }
}
</style>
